<template>
  <VPopper
    ref="popper"
    :lazy
    :mount-to
    :open-delay
    :placement
    :popper-class="`v-popper--tooltip v-popper--tooltip--${type}`"
    :reference-class
    :reference-style
    show-arrow
    :tag
    :transition="showOnOverflow && !overflowing ? undefined : 'v-fade'"
    :trigger="isDisabled ? 'disabled' : trigger"
    :wrapper-tag
  >
    <template #reference>
      <slot />
    </template>

    <div :class="classes">
      <slot v-if="!overflowing || content || $slots.content" name="content">
        {{ content }}
      </slot>
      <template v-else>{{ overflowingContent }}</template>
    </div>
  </VPopper>
</template>

<script setup lang="ts">
import { debounce } from 'lodash-es'
import { computed, onMounted, ref, type StyleValue, useTemplateRef, watch } from 'vue'

import { useResizeObserver } from '../composables/useResizeObserver.ts'
import type { VPopperPlacement, VPopperTrigger, VTooltipType } from '../types.ts'
import VPopper from './VPopper.vue'

interface Props {
  content?: string
  placement?: VPopperPlacement
  trigger?: VPopperTrigger
  openDelay?: number
  referenceStyle?: StyleValue
  referenceClass?: any
  tag?: string
  type?: VTooltipType
  wrapperTag?: string
  mountTo?: string | HTMLElement
  overflowTarget?: string
  overflowContentTarget?: string
  disabled?: boolean
  lazy?: boolean
  showOnOverflow?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  content: '',
  placement: 'top',
  trigger: 'hover',
  openDelay: 0,
  referenceStyle: '',
  referenceClass: '',
  tag: 'div',
  type: 'light',
  wrapperTag: 'div',
  mountTo: '#v-poppers',
  overflowTarget: undefined,
  overflowContentTarget: undefined,
  disabled: false,
  lazy: false,
  showOnOverflow: false,
})

const slots = defineSlots<{
  default?(): any
  content?(): any
}>()

const popper = useTemplateRef('popper')
const overflowing = ref(false)
const overflowingContent = ref('')

const classes = computed(() => ({
  'v-tooltip': true,
  [`v-tooltip--${props.type}`]: props.type,
}))

const isDisabled = computed(() => props.disabled || (props.showOnOverflow && !overflowing.value) || (!slots.content && !props.content && !overflowingContent.value))

watch(isDisabled, (disabled) => {
  if (disabled) {
    popper.value?.hide()
  }
})

function originalOnResize({ width }: { width: number }) {
  if (!popper.value) {
    return
  }

  const reference = popper.value.reference

  if (!reference) {
    return
  }

  if (props.overflowTarget) {
    const target = reference.querySelector(props.overflowTarget) as HTMLElement
    if (!target) {
      return
    }

    overflowing.value = target.offsetWidth < target.scrollWidth

    if (overflowing.value) {
      if (props.overflowContentTarget) {
        overflowingContent.value = reference.querySelector(props.overflowContentTarget)?.textContent ?? ''
      } else {
        overflowingContent.value = target.textContent ?? ''
      }
    }
  } else {
    overflowing.value = width < reference.scrollWidth
    overflowingContent.value = reference.textContent ?? ''
  }

  if (!overflowing.value) {
    overflowingContent.value = ''
  }
}

const onResize = debounce(originalOnResize, 300)

onMounted(() => {
  if (props.showOnOverflow && popper.value?.reference) {
    useResizeObserver(popper.value?.reference, onResize)
  }
})

defineExpose({
  popper,
  originalOnResize,
  overflowing,
  overflowingContent,
})
</script>

<style>
.v-tooltip {
  border-radius: var(--border-radius);
  padding: 10px;
  font-size: 12px;
}

.v-tooltip--dark {
  color: #fff;
  background-color: #333;
}

.v-tooltip--light {
  background-color: #fff;
  border: 1px solid var(--color-grey-20);
}

.v-popper--tooltip--light {
  filter: var(--drop-shadow-light);
}

.v-popper--tooltip--dark .v-popper__arrow::after {
  background-color: #333;
}

.v-popper--tooltip--light .v-popper__arrow::after {
  background-color: #fff;
}
</style>
